import type { Locale, Theme } from '../types';
import { isLocale } from '../i18n';

const LS_LOCALE = 'sas.locale';
const LS_THEME = 'sas.theme';

interface State {
  locale: Locale;
  theme: Theme;
  /** Current route path, e.g. 'national', 'education', 'education/quality'. */
  path: string;
}

type Listener = (state: State) => void;

function readLocale(): Locale {
  const stored = safeGet(LS_LOCALE);
  if (stored && isLocale(stored)) return stored;
  const env = (import.meta.env.VITE_DEFAULT_LOCALE as string) || 'ar';
  return isLocale(env) ? env : 'ar';
}

function readTheme(): Theme {
  return safeGet(LS_THEME) === 'dark' ? 'dark' : 'light';
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

/** Minimal observable application store (single source of UI state). */
class Store {
  private state: State = { locale: readLocale(), theme: readTheme(), path: 'national' };
  private listeners = new Set<Listener>();

  get(): Readonly<State> {
    return this.state;
  }
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  setLocale(locale: Locale): void {
    if (locale === this.state.locale) return;
    this.state = { ...this.state, locale };
    safeSet(LS_LOCALE, locale);
    this.emit();
  }
  setTheme(theme: Theme): void {
    if (theme === this.state.theme) return;
    this.state = { ...this.state, theme };
    safeSet(LS_THEME, theme);
    this.emit();
  }
  setPath(path: string): void {
    if (path === this.state.path) return;
    this.state = { ...this.state, path };
    this.emit();
  }
  private emit(): void {
    for (const fn of this.listeners) fn(this.state);
  }
}

export const store = new Store();
